import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { StarIcon } from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { smsService } from '../../services/smsService';
import { SmsConversation, SmsMessage } from '../../types/sms';
import LoadingSpinner from '../UI/LoadingSpinner';
import { formatApiErrorMessage } from '../../utils/formatApiError';
import { formatLeadStatusLabel, leadStatusPillClass } from '../../constants/pipelineStatuses';
import FollowUpDateEditor from '../Leads/FollowUpDateEditor';
import LeadStatusChangeMenu from '../Leads/LeadStatusChangeMenu';
import MerchantFilePanel from './MerchantFilePanel';
import { useWebRTC } from '../../contexts/WebRTCContext';

function formatFullTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

function getDateLabel(iso: string): string {
  try {
    const d = new Date(iso);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (d.toDateString() === today.toDateString()) return 'Today';
    if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return d.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}

function MsgStatusIcon({ status }: { status: string }) {
  if (status === 'delivered') return <span className="text-[10px] font-bold text-primary">✓✓</span>;
  if (status === 'sent') return <span className="text-[10px] text-muted-foreground">✓</span>;
  if (status === 'failed' || status === 'undelivered') {
    return <span className="text-[10px] text-destructive font-bold">✗</span>;
  }
  return <span className="text-[10px] text-muted-foreground">○</span>;
}

function isPlaceholderName(value?: string | null): boolean {
  const v = value?.trim().toLowerCase();
  return !v || v === 'na' || v === 'n/a' || v === 'unknown' || v === 'unknown lead';
}

function resolveMerchantName(leadName?: string, leadPhone?: string): string {
  if (!isPlaceholderName(leadName)) return leadName!.trim();
  return leadPhone?.trim() || '—';
}

function resolveCompanyName(companyName?: string | null): string {
  const value = companyName?.trim();
  return value || '—';
}

function outboundSenderLabel(msg: SmsMessage): string | null {
  if (msg.direction !== 'outbound') return null;
  if (msg.sent_by_agent_id) {
    const name = msg.sent_by_agent_name?.trim() || 'Staff';
    const role = msg.sent_by_agent_role;
    if (role === 'admin' || role === 'owner') return `Admin · ${name}`;
    if (role === 'agent') return `Agent · ${name}`;
    return `Sent by · ${name}`;
  }
  const step = msg.sequence_step_number != null ? Number(msg.sequence_step_number) : null;
  if (step === 1) return 'Campaign · Initial message';
  if (step != null && step > 1) return `Campaign · Follow-up ${step - 1}`;
  return 'Campaign · Automated';
}

export interface SmsConversationPanelProps {
  conversation: SmsConversation;
  wsRef: React.RefObject<WebSocket | null>;
  onAssigned: (id: string, agentName?: string) => void;
  onLeadUpdated: (id: string, patch: Partial<SmsConversation>) => void;
  onToggleStar: (id: string, starred: boolean) => void;
  currentUserId?: string;
  currentUserRole?: string;
  currentUserName?: string;
}

const SmsConversationPanel: React.FC<SmsConversationPanelProps> = ({
  conversation,
  wsRef,
  onAssigned,
  onLeadUpdated,
  onToggleStar,
  currentUserId,
  currentUserRole,
  currentUserName,
}) => {
  const [messages, setMessages] = useState<SmsMessage[]>([]);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [followUpOpen, setFollowUpOpen] = useState(false);
  const [showChangeStatus, setShowChangeStatus] = useState(false);
  const [showFilePanel, setShowFilePanel] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [calling, setCalling] = useState(false);
  const [sendingApp, setSendingApp] = useState(false);
  const [showStarredOnly, setShowStarredOnly] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const topSentinelRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const initialScrollDone = useRef(false);
  const { makeOutboundCall, status: webrtcStatus } = useWebRTC();

  const isAdmin = currentUserRole === 'admin' || currentUserRole === 'owner';
  const isAssignedToMe =
    Boolean(currentUserId) && conversation.assigned_agent_id === currentUserId;
  const needsAssign = !isAdmin && currentUserRole === 'agent' && !isAssignedToMe;

  const scrollToBottom = useCallback(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const loadMessages = useCallback(async () => {
    setLoading(true);
    initialScrollDone.current = false;
    try {
      const result = await smsService.getConversationMessages(conversation.id, {
        limit: 50,
        starredOnly: showStarredOnly || undefined,
      });
      setMessages(result.messages);
      setHasMoreMessages(result.hasMore);
    } catch {
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  }, [conversation.id, showStarredOnly]);

  const loadOlderMessages = useCallback(async () => {
    if (loadingOlder || !hasMoreMessages || messages.length === 0) return;
    const oldest = messages[0];
    if (!oldest) return;

    setLoadingOlder(true);
    const container = scrollContainerRef.current;
    const prevScrollHeight = container?.scrollHeight ?? 0;

    try {
      const result = await smsService.getConversationMessages(conversation.id, {
        limit: 50,
        before: oldest.id,
        starredOnly: showStarredOnly || undefined,
      });
      setMessages((prev) => {
        const existing = new Set(prev.map((m) => m.id));
        const older = result.messages.filter((m) => !existing.has(m.id));
        return [...older, ...prev];
      });
      setHasMoreMessages(result.hasMore);
      requestAnimationFrame(() => {
        if (!container) return;
        container.scrollTop += container.scrollHeight - prevScrollHeight;
      });
    } catch {
      toast.error('Failed to load older messages');
    } finally {
      setLoadingOlder(false);
    }
  }, [conversation.id, hasMoreMessages, loadingOlder, messages, showStarredOnly]);

  const handleToggleMessageStar = async (messageId: string, starred: boolean) => {
    try {
      await smsService.setMessageStarred(messageId, starred);
      setMessages((prev) => {
        if (showStarredOnly && !starred) {
          return prev.filter((m) => m.id !== messageId);
        }
        return prev.map((m) => (m.id === messageId ? { ...m, is_starred: starred } : m));
      });
    } catch {
      toast.error('Failed to star message');
    }
  };

  useEffect(() => {
    setMessages([]);
    setHasMoreMessages(false);
    setShowStarredOnly(false);
    setMenuOpen(false);
    setFollowUpOpen(false);
    setShowChangeStatus(false);
    setShowFilePanel(false);
    loadMessages();
  }, [loadMessages]);

  useEffect(() => {
    if (loading || initialScrollDone.current) return;
    initialScrollDone.current = true;
    scrollToBottom();
  }, [loading, messages, scrollToBottom]);

  useEffect(() => {
    const sentinel = topSentinelRef.current;
    const container = scrollContainerRef.current;
    if (!sentinel || !container || loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          void loadOlderMessages();
        }
      },
      { root: container, rootMargin: '120px 0px 0px 0px', threshold: 0 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadOlderMessages, loading]);

  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
        setShowChangeStatus(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  useEffect(() => {
    const ws = wsRef.current;
    if (!ws) return;
    const handler = (event: MessageEvent) => {
      try {
        const parsed = JSON.parse(event.data);
        if (
          parsed?.type === 'sms:message_created' &&
          parsed?.payload?.conversationId === conversation.id &&
          parsed?.payload?.message
        ) {
          const incoming: SmsMessage = parsed.payload.message;
          setMessages((prev) => {
            if (prev.some((m) => m.id === incoming.id)) return prev;
            return [...prev, incoming];
          });
          requestAnimationFrame(() => scrollToBottom());
        }
      } catch {
        /* ignore */
      }
    };
    ws.addEventListener('message', handler);
    return () => ws.removeEventListener('message', handler);
  }, [conversation.id, wsRef]);

  const onSend = async () => {
    if (!draft.trim()) return;
    setSending(true);
    try {
      const sent = await smsService.sendReply(conversation.id, draft.trim());
      setMessages((prev) => (prev.some((m) => m.id === sent.id) ? prev : [...prev, sent]));
      setDraft('');
      onLeadUpdated(conversation.id, { last_message_preview: sent.body, last_message_at: sent.created_at });
      if (textareaRef.current) textareaRef.current.style.height = 'auto';
    } catch (error: unknown) {
      toast.error(formatApiErrorMessage(error, 'Failed to send message'));
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };

  const handleAssignToMe = async () => {
    setAssigning(true);
    try {
      await smsService.assignConversationToMe(conversation.id);
      onAssigned(conversation.id, currentUserName);
      toast.success('Conversation assigned to you');
    } catch (error: unknown) {
      toast.error(formatApiErrorMessage(error, 'Failed to assign conversation'));
    } finally {
      setAssigning(false);
    }
  };

  const handleCallMerchant = async () => {
    setCalling(true);
    setMenuOpen(false);
    try {
      if (webrtcStatus !== 'ready' && webrtcStatus !== 'in-call') {
        toast.error('WebRTC not connected. Please wait or refresh.');
        return;
      }
      const data = await smsService.prepareConversationCall(conversation.id);
      makeOutboundCall({
        destinationNumber: data.fromNumber,
        callerNumber: data.fromNumber,
        callAttemptId: data.callAttemptId,
        routingAttemptId: data.routingAttemptId,
        routingLegId: data.routingLegId,
        campaignId: data.campaignId,
        leadId: data.leadId,
        leadName: data.leadName,
        leadPhone: data.leadPhone || data.toNumber,
        leadEmail: data.leadEmail || null,
        companyName: data.companyName || conversation.company_name || null,
        campaignName: data.campaignName || null,
        scriptName: data.scriptName || null,
        scriptContent: data.scriptContent || null,
        scriptStructure: data.scriptStructure || null,
        agentId: data.agentId,
        leadStatus: data.leadStatus || conversation.lead_status || null,
        subStageId: data.subStageId || conversation.sub_stage_id || null,
        dispositionMode: 'results_callback',
      });
      toast.success(`Dialing ${conversation.lead_name || 'merchant'}…`);
    } catch (error: unknown) {
      toast.error(formatApiErrorMessage(error, 'Failed to start call'));
    } finally {
      setCalling(false);
    }
  };

  const handleSendApp = async () => {
    setSendingApp(true);
    setMenuOpen(false);
    try {
      const result = await smsService.sendApp(conversation.id);
      setMessages((prev) =>
        prev.some((m) => m.id === result.message.id) ? prev : [...prev, result.message]
      );
      onLeadUpdated(conversation.id, {
        lead_status: result.leadStatus,
        last_message_preview: result.message.body,
        last_message_at: result.message.created_at,
      });
      toast.success('Application link sent');
    } catch (error: unknown) {
      toast.error(formatApiErrorMessage(error, 'Failed to send application'));
    } finally {
      setSendingApp(false);
    }
  };

  return (
    <div className="flex flex-col h-full min-h-0 bg-card/80 backdrop-blur-xl border border-border overflow-hidden shadow-lg w-full max-w-full rounded-[20px] lg:rounded-[28px]">
      <div className="px-5 py-4 border-b border-border flex justify-between items-start shrink-0 gap-3">
        <div className="min-w-0 flex-1">
          <div className="space-y-1">
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground shrink-0">
                Merchant name
              </span>
              <span className="text-base font-semibold text-foreground truncate">
                {resolveMerchantName(conversation.lead_name, conversation.lead_phone)}
              </span>
            </div>
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground shrink-0">
                Company name
              </span>
              <span className="text-base font-semibold text-foreground truncate">
                {resolveCompanyName(conversation.company_name)}
              </span>
            </div>
            {conversation.lead_phone && (
              <p className="text-xs text-muted-foreground pt-0.5">{conversation.lead_phone}</p>
            )}
          </div>
          <div className="flex flex-wrap gap-2 mt-3 items-center">
            <span
              className={`px-3 py-1 rounded-full text-xs font-medium ${leadStatusPillClass(conversation.lead_status)}`}
            >
              {formatLeadStatusLabel(conversation.lead_status)}
              {conversation.sub_stage_name ? ` · ${conversation.sub_stage_name}` : ''}
            </span>
            {needsAssign && (
              <button
                type="button"
                onClick={handleAssignToMe}
                disabled={assigning}
                className="px-3 py-1 rounded-full text-xs font-medium bg-primary/15 text-primary hover:bg-primary/25 disabled:opacity-50"
              >
                {assigning ? 'Assigning…' : 'Assign to me'}
              </button>
            )}
          </div>
        </div>

        <div className="flex items-start gap-2 shrink-0">
          <button
            type="button"
            onClick={() => onToggleStar(conversation.id, !conversation.is_starred)}
            className="w-12 h-12 rounded-2xl bg-muted hover:bg-muted/80 flex items-center justify-center transition-colors"
            title={conversation.is_starred ? 'Remove star' : 'Star conversation'}
            aria-label={conversation.is_starred ? 'Remove star' : 'Star conversation'}
          >
            {conversation.is_starred ? (
              <StarIconSolid className="h-6 w-6 text-amber-500" />
            ) : (
              <StarIcon className="h-6 w-6 text-muted-foreground hover:text-amber-500" />
            )}
          </button>

          <div className="relative" ref={menuRef}>
          <button
            type="button"
            onClick={() => {
              setMenuOpen((v) => !v);
              setShowChangeStatus(false);
            }}
            className="w-12 h-12 rounded-2xl bg-muted hover:bg-muted/80 text-muted-foreground flex items-center justify-center text-xl font-bold"
          >
            ⋯
          </button>

          {menuOpen && (
            <div className="absolute right-0 top-14 w-56 bg-popover border border-border rounded-2xl shadow-xl p-1.5 z-30 space-y-0.5">
              <button
                type="button"
                disabled={calling}
                className="w-full flex items-center gap-3 hover:bg-muted rounded-xl px-3 py-2.5 text-sm text-left disabled:opacity-50"
                onClick={handleCallMerchant}
              >
                <span>📞</span>
                <span>{calling ? 'Calling…' : 'Call Merchant'}</span>
              </button>

              <button
                type="button"
                disabled={sendingApp || needsAssign}
                className="w-full flex items-center gap-3 hover:bg-muted rounded-xl px-3 py-2.5 text-sm text-left disabled:opacity-50"
                onClick={handleSendApp}
              >
                <span>📄</span>
                <span>{sendingApp ? 'Sending…' : 'Send App'}</span>
              </button>

              <button
                type="button"
                className="w-full flex items-center gap-3 hover:bg-muted rounded-xl px-3 py-2.5 text-sm text-left"
                onClick={() => {
                  setShowFilePanel(true);
                  setMenuOpen(false);
                }}
              >
                <span>📁</span>
                <span>View File</span>
              </button>

              <button
                type="button"
                className="w-full flex items-center gap-3 hover:bg-muted rounded-xl px-3 py-2.5 text-sm text-left"
                onClick={() => {
                  setMenuOpen(false);
                  setFollowUpOpen(true);
                }}
              >
                <span>📅</span>
                <span>Follow Up Date</span>
              </button>

              <button
                type="button"
                className="w-full flex items-center gap-3 hover:bg-muted rounded-xl px-3 py-2.5 text-sm text-left"
                onClick={() => {
                  setMenuOpen(false);
                  setShowStarredOnly((v) => !v);
                }}
              >
                <span>⭐</span>
                <span>{showStarredOnly ? 'Show all messages' : 'Starred messages'}</span>
              </button>

              <button
                type="button"
                className="w-full flex items-center gap-3 hover:bg-muted rounded-xl px-3 py-2.5 text-sm text-left"
                onClick={() => {
                  setShowChangeStatus((v) => !v);
                }}
              >
                <span>🔄</span>
                <span>Change Status</span>
              </button>
              {showChangeStatus && (
                <LeadStatusChangeMenu
                  leadId={conversation.lead_id}
                  campaignId={conversation.campaign_id}
                  currentStatus={conversation.lead_status}
                  subStageId={conversation.sub_stage_id}
                  subStageName={conversation.sub_stage_name}
                  onUpdated={(patch) => {
                    onLeadUpdated(conversation.id, {
                      lead_status: patch.status,
                      sub_stage_id: patch.subStageId,
                      sub_stage_name: patch.subStageName,
                    });
                  }}
                  onClose={() => {
                    setShowChangeStatus(false);
                    setMenuOpen(false);
                  }}
                  onRequestFollowUp={() => {
                    setMenuOpen(false);
                    setFollowUpOpen(true);
                  }}
                />
              )}
            </div>
          )}
          </div>
        </div>
      </div>

      <div
        ref={scrollContainerRef}
        className="flex-1 overflow-y-auto px-5 py-5 space-y-4 bg-background/50 scroll-area"
      >
        {!loading && hasMoreMessages && (
          <div ref={topSentinelRef} className="flex justify-center py-1">
            {loadingOlder ? (
              <LoadingSpinner size="sm" />
            ) : (
              <span className="text-[11px] text-muted-foreground">Scroll up for older messages</span>
            )}
          </div>
        )}
        {loading && (
          <div className="flex items-center justify-center h-32">
            <LoadingSpinner size="md" />
          </div>
        )}
        {showStarredOnly && !loading && (
          <div className="flex items-center justify-between gap-3 rounded-2xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200/60 dark:border-amber-800/40 px-4 py-2.5 mb-2">
            <span className="text-sm font-medium text-amber-800 dark:text-amber-300">Starred messages</span>
            <button
              type="button"
              onClick={() => setShowStarredOnly(false)}
              className="text-xs font-semibold text-amber-700 dark:text-amber-400 hover:underline"
            >
              Show all
            </button>
          </div>
        )}
        {!loading && messages.length === 0 && (
          <div className="text-sm text-muted-foreground text-center py-16">
            {showStarredOnly ? 'No starred messages in this conversation.' : 'No messages yet.'}
          </div>
        )}
        {messages.map((msg, idx) => {
          const prevMsg = messages[idx - 1];
          const showDateSep =
            !prevMsg || getDateLabel(msg.created_at) !== getDateLabel(prevMsg.created_at);
          return (
            <React.Fragment key={msg.id}>
              {showDateSep && (
                <div className="flex items-center gap-3 my-2">
                  <div className="flex-1 h-px bg-border" />
                  <span className="text-[11px] text-muted-foreground font-medium px-2">
                    {getDateLabel(msg.created_at)}
                  </span>
                  <div className="flex-1 h-px bg-border" />
                </div>
              )}
              <div
                className={`group flex items-end gap-1.5 max-w-[75%] md:max-w-[60%] ${
                  msg.direction === 'outbound' ? 'ml-auto flex-row-reverse' : ''
                }`}
              >
                <button
                  type="button"
                  onClick={() => handleToggleMessageStar(msg.id, !msg.is_starred)}
                  className={`shrink-0 mb-5 p-1 rounded-full transition-opacity ${
                    msg.is_starred ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                  }`}
                  title={msg.is_starred ? 'Unstar message' : 'Star message'}
                  aria-label={msg.is_starred ? 'Unstar message' : 'Star message'}
                >
                  {msg.is_starred ? (
                    <StarIconSolid className="h-4 w-4 text-amber-500" />
                  ) : (
                    <StarIcon className="h-4 w-4 text-muted-foreground hover:text-amber-500" />
                  )}
                </button>
                <div
                  className={`flex flex-col min-w-0 ${
                    msg.direction === 'outbound' ? 'items-end' : 'items-start'
                  }`}
                >
                  {msg.direction === 'outbound' && outboundSenderLabel(msg) && (
                    <span className="text-[10px] font-medium text-muted-foreground mb-1 px-1">
                      {outboundSenderLabel(msg)}
                    </span>
                  )}
                  <div
                    className={`px-4 py-3 rounded-[24px] text-sm leading-relaxed shadow-sm ${
                      msg.direction === 'outbound'
                        ? 'bg-primary text-primary-foreground rounded-br-md'
                        : 'bg-card text-foreground border border-border rounded-bl-md'
                    }`}
                  >
                    {msg.body}
                  </div>
                  <div className="flex items-center gap-1.5 mt-1 px-1">
                    <span className="text-[10px] text-muted-foreground">{formatFullTime(msg.created_at)}</span>
                    {msg.is_starred && <StarIconSolid className="h-3 w-3 text-amber-500" />}
                    {msg.direction === 'outbound' && msg.status && <MsgStatusIcon status={msg.status} />}
                  </div>
                </div>
              </div>
            </React.Fragment>
          );
        })}
        <div ref={bottomRef} />
      </div>

      <div className="shrink-0 px-5 py-4 border-t border-border">
        <div className="flex gap-2 items-end bg-muted rounded-3xl px-2 py-2">
          <textarea
            ref={textareaRef}
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              const ta = textareaRef.current;
              if (ta) {
                ta.style.height = 'auto';
                ta.style.height = `${Math.min(ta.scrollHeight, 120)}px`;
              }
            }}
            onKeyDown={handleKeyDown}
            rows={1}
            className="flex-1 px-3 py-2 outline-none resize-none text-sm text-foreground bg-transparent placeholder-muted-foreground"
            placeholder="Type a message..."
          />
          <button
            type="button"
            onClick={onSend}
            disabled={sending || !draft.trim() || needsAssign}
            className="rounded-2xl px-6 py-2.5 bg-primary hover:opacity-90 text-primary-foreground text-sm font-semibold shrink-0 disabled:opacity-40"
          >
            {sending ? '…' : 'Send'}
          </button>
        </div>
        {needsAssign && (
          <p className="text-xs text-amber-600 dark:text-amber-400 mt-2 px-1">
            Assign this thread to yourself before sending.
          </p>
        )}
      </div>

      {showFilePanel && (
        <MerchantFilePanel
          leadId={conversation.lead_id}
          leadName={conversation.lead_name}
          companyName={conversation.company_name || undefined}
          onClose={() => setShowFilePanel(false)}
        />
      )}

      <FollowUpDateEditor
        leadId={conversation.lead_id}
        leadName={conversation.lead_name}
        followUpDate={conversation.follow_up_date}
        open={followUpOpen}
        onOpenChange={setFollowUpOpen}
        hideTrigger
        onUpdated={(_leadId, followUpDate) => {
          onLeadUpdated(conversation.id, { follow_up_date: followUpDate });
          setFollowUpOpen(false);
        }}
      />
    </div>
  );
};

export default SmsConversationPanel;
